import Tooltip from 'antd/es/tooltip';
import { useMemo, useState } from 'react';

import { isCryptoAccount } from 'utils/accounts/isCryptoAccount';

import { SandboxAddFundsWrapper } from '@sandbox/SandboxAddFundsWrapper';
import { AccountStatus } from 'api';
import { Restricted } from 'containers/Restricted';

import { useVirtualAccounts } from 'hooks/context/useVirtualAccounts';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { TransactionTwoWayIcon } from '@atoms/Icon';

import { AccountTransactionsFilters } from './AccountTransactionsFilters';
import { INTER_TRANSFER_BUTTON_TEST_ID } from './constants';
import type { FiltersState } from './localState';
import { TransferDialog } from './TransferDialog/TransferDialog';

type Props = {
  accountNumber: string;
  filters: FiltersState;
  setFilters: (value: FiltersState) => void;
  resetState: () => void;
};

export const AccountTransactionsHeader = ({
  accountNumber,
  filters,
  setFilters,
  resetState
}: Props) => {
  const [isTransferOpen, setIsTransferOpen] = useState(false);

  const { findAccountByAccountNumber } = useVirtualAccounts();

  const currentAccountData = useMemo(
    () => findAccountByAccountNumber(accountNumber),
    [accountNumber, findAccountByAccountNumber]
  );

  const transferDisabledReason = useMemo(() => {
    if (currentAccountData?.status === AccountStatus.suspended) {
      return 'Transfers are unavailable for suspended accounts.';
    }

    if (currentAccountData?.status === AccountStatus.terminated) {
      return 'Transfers are unavailable for terminated accounts.';
    }

    if (currentAccountData && isCryptoAccount(currentAccountData)) {
      return 'Transfers are unavailable for crypto accounts.';
    }

    return undefined;
  }, [currentAccountData]);

  return (
    <div className="relative flex w-full items-center justify-between">
      <div className="flex flex-col">
        <div className="mb-6 h-10 text-2xl font-medium">
          Account Transactions
        </div>
        <AccountTransactionsFilters {...{ filters, setFilters, resetState }} />
      </div>

      {currentAccountData && (
        <>
          <div className="absolute right-0 top-0 flex gap-2">
            <SandboxAddFundsWrapper accountNumber={accountNumber} />

            <Restricted requiredPermission="accounts:manage">
              <Tooltip title={transferDisabledReason}>
                <span>
                  <PrimaryButton
                    icon={
                      <TransactionTwoWayIcon className="size-4 stroke-current" />
                    }
                    data-testid={INTER_TRANSFER_BUTTON_TEST_ID}
                    onClick={() => setIsTransferOpen(true)}
                    disabled={Boolean(transferDisabledReason)}
                  >
                    Transfer
                  </PrimaryButton>
                </span>
              </Tooltip>
            </Restricted>
          </div>

          <TransferDialog
            open={isTransferOpen}
            setOpen={setIsTransferOpen}
            defaultFromAccountNumber={currentAccountData.account_number}
          />
        </>
      )}
    </div>
  );
};
